import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import {
  ICreateOperationTriggerRequest,
  IOperationTriggerResponse,
} from 'types/api/operationsAimApi.interface'
import { IOperationTriggersSlice } from 'types/operationTriggers.interface'
import { IOpsTriggerRow } from 'types/operationsControl.interface'
import { baseUrl } from 'utils/axios'
import { handleErrors } from 'utils/helpers'
import { mapTrigger } from 'pages/Operations/Shared/opsApiMappers'
import { USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import { DEMO_TRIGGERS } from 'pages/Operations/Shared/opsApiDemoCore'
const initialState: IOperationTriggersSlice = {
  isLoading: false,
  triggers: [],
}

export const getOperationTriggers = createAsyncThunk(
  'operationTriggers/getOperationTriggers',
  async (_, thunkAPI) => {
    if (USE_OPS_MOCKS) return DEMO_TRIGGERS
    try {
      const resp: AxiosResponse<IOperationTriggerResponse[]> = await baseUrl.get('aim-operations/trigger')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createOperationTrigger = createAsyncThunk(
  'operationTriggers/createOperationTrigger',
  async (body: ICreateOperationTriggerRequest, thunkAPI) => {
    try {
      await baseUrl.post('aim-operations/trigger', body)
      toast.success('Trigger created')
      await thunkAPI.dispatch(getOperationTriggers())
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const patchOperationTrigger = createAsyncThunk(
  'operationTriggers/patchOperationTrigger',
  async ({ id, body }: { id: number; body: Partial<ICreateOperationTriggerRequest> }, thunkAPI) => {
    try {
      await baseUrl.patch(`aim-operations/trigger/${id}`, body)
      await thunkAPI.dispatch(getOperationTriggers())
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteOperationTrigger = createAsyncThunk(
  'operationTriggers/deleteOperationTrigger',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete(`aim-operations/trigger/${id}`)
      toast.success('Trigger deleted')
      await thunkAPI.dispatch(getOperationTriggers())
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const toggleOperationTrigger = createAsyncThunk(
  'operationTriggers/toggleOperationTrigger',
  async ({ id, enabled }: { id: number; enabled: boolean }, thunkAPI) => {
    try {
      await baseUrl.patch(`aim-operations/trigger/${id}`, { enabled })
      await thunkAPI.dispatch(getOperationTriggers())
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const operationTriggersSlice = createSlice({
  name: 'operationTriggers',
  initialState,
  reducers: {
    clearOperationTriggers: (state) => {
      state.triggers = []
    },
    addTrigger: (state, action: PayloadAction<Omit<IOpsTriggerRow, 'id' | 'fireCount' | 'lastFired'>>) => {
      const nextId = state.triggers.reduce((max, t) => Math.max(max, t.id), 0) + 1
      state.triggers = [{ ...action.payload, id: nextId, fireCount: 0, lastFired: null }, ...state.triggers]
    },
    toggleTrigger: (state, action: PayloadAction<number>) => {
      state.triggers = state.triggers.map((t) =>
        t.id === action.payload ? { ...t, enabled: !t.enabled } : t,
      )
    },
    deleteTrigger: (state, action: PayloadAction<number>) => {
      state.triggers = state.triggers.filter((t) => t.id !== action.payload)
    },
  },
  extraReducers: {
    [getOperationTriggers.pending.type]: (state) => {
      state.isLoading = true
    },
    [getOperationTriggers.fulfilled.type]: (
      state,
      { payload }: { payload: IOperationTriggerResponse[] },
    ) => {
      state.isLoading = false
      state.triggers = (payload || []).map(mapTrigger)
    },
    [getOperationTriggers.rejected.type]: (state, action) => {
      state.isLoading = false
      handleErrors(action.payload)
    },
    [createOperationTrigger.rejected.type]: (state, action) => handleErrors(action.payload),
    [deleteOperationTrigger.rejected.type]: (state, action) => handleErrors(action.payload),
    [toggleOperationTrigger.rejected.type]: (state, action) => handleErrors(action.payload),
  },
})

export const { clearOperationTriggers, addTrigger, toggleTrigger, deleteTrigger } =
  operationTriggersSlice.actions
export default operationTriggersSlice.reducer
